import clsx from "clsx"
import { forwardRef } from "react"

import useDebouncedCallback from "@hooks/useDebouncedCallback"

import { faIcon } from "@utils/faIcon"

import { Skeleton } from "@particles/index"

import { Icon } from "@atoms"

import styles from "./IconButton.module.scss"
import { IconButtonProps } from "./IconButton.types"

const ICON_SIZE = 14

function IconButton(props: IconButtonProps, ref: React.Ref<HTMLButtonElement>) {
    const {
        size,
        variant,
        type = "button",
        className,

        isTabbable = true,

        icon,
        iconOnActive,
        iconIsActive = false,
        iconClassName,
        iconRotation,

        onClick,
        noDebounce = false,

        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isProcessing = false,
        isDisabled = false,
        isLoading = false,

        ...rest
    } = props

    const debouncedClick = useDebouncedCallback(onClick)

    return (
        <button
            className={clsx(
                styles.base,
                className,
                {
                    [styles.isLoading]: isLoading,
                    [styles.isProcessing]: isProcessing,
                    [styles.isFocused]: _isFocused,
                    [styles.isHovered]: _isHovered,
                    [styles.isPressed]: isPressed,
                },
                styles[`${variant}Variant`],
                styles[`${size}Size`],
            )}
            type={type}
            onClick={noDebounce ? onClick : debouncedClick}
            disabled={isDisabled || isProcessing || isLoading}
            aria-disabled={isDisabled}
            aria-busy={isProcessing || isLoading}
            tabIndex={isTabbable && !isDisabled ? 0 : -1}
            ref={ref}
            {...rest}
        >
            {isLoading ? (
                <div className={styles.skeleton}>
                    <Skeleton circle={true} height={ICON_SIZE} width={ICON_SIZE} enableAnimation={true} />
                </div>
            ) : (
                <Icon
                    container="fixedHeight"
                    icon={isProcessing ? faIcon({ name: "spinner-third", style: "regular" }) : icon}
                    iconOnActive={isProcessing ? undefined : iconOnActive}
                    isActive={iconIsActive}
                    className={iconClassName}
                    rotation={iconRotation}
                    spin={isProcessing}
                    size={ICON_SIZE}
                />
            )}
        </button>
    )
}

export default forwardRef(IconButton)
